import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { ACL_TYPE } from '@openx/types/aclType';
import { type DealFormData, dealFormField } from '@openx/types/deal';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { type ChangeEvent, type JSX, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { generatedIdTypes, idOptions } from './idsFormComponents/enums';
import { IdCustomRadio } from './idsFormComponents/IdCustomRadio';
import { IdGeneratedRadio } from './idsFormComponents/IdGeneratedRadio';
import { IdGroupPreview } from './idsFormComponents/IdGroupPreview';
import { IdGroupRadio } from './idsFormComponents/IdGroupRadio';
import { IdPreview } from './idsFormComponents/IdPreview';
import { ReadOnlyIdSectionContent } from './idsFormComponents/ReadOnlyIdSectionContent';

type IdsFormSectionProps = {
  isEdit: boolean;
};

type LocationState = {
  externalDealId?: string;
};

export const IdsFormSection = ({ isEdit }: IdsFormSectionProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { useIsAllowed, isOa } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { setValue, control } = useFormContext<DealFormData>();
  const location = useLocation();

  const observableDealIdType = useWatch({ control, name: `${dealFormField.DEAL_ID_TYPE_RADIO}` });
  const isInternal = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));

  const updateDealTypeOption = useCallback(
    (_: ChangeEvent<HTMLInputElement>, option: string): void => {
      setValue(`${dealFormField.DEAL_ID_TYPE_RADIO}`, option, { shouldValidate: true });

      switch (option) {
        case idOptions.GENERATED:
          setValue(`${dealFormField.CUSTOM_DEAL_ID}`, '', { shouldValidate: true });
          setValue(`${dealFormField.EXTERNAL_DEAL_ID}`, '', { shouldValidate: true });
          break;

        case idOptions.CUSTOM:
          setValue(`${dealFormField.GENERATED_DEAL_ID_PREFIX}`, generatedIdTypes.Default);
          setValue(`${dealFormField.EXTERNAL_DEAL_ID}`, '', { shouldValidate: true });
          break;

        case idOptions.EXTERNAL:
          setValue(`${dealFormField.GENERATED_DEAL_ID_PREFIX}`, generatedIdTypes.Default);
          setValue(`${dealFormField.CUSTOM_DEAL_ID}`, '', { shouldValidate: true });
          break;
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (location.state?.externalDealId) {
      requestAnimationFrame(() => {
        setValue(dealFormField.DEAL_ID_TYPE_RADIO, idOptions.EXTERNAL, { shouldValidate: true });
        setValue(dealFormField.EXTERNAL_DEAL_ID, (location.state as LocationState).externalDealId);
      });
    }
  }, [location.state, setValue]);

  if (isEdit) return <ReadOnlyIdSectionContent />;

  return isInternal || isOa ? (
    <FormSection
      divider
      title={t('IDs')}
      rightColumnSize={RIGHT_COLUMN_WIDTH + 1}
      description={t('Please select a Deal ID type.')}
    >
      <RHFRadioField
        sx={{ maxWidth: '672px', width: 'calc(100% + 30px)' }}
        name={dealFormField.DEAL_ID_TYPE_RADIO}
        data-test="deal-id-radio-group"
        options={[
          {
            'data-test': 'radio-generated',
            label: (
              <>
                <IdGeneratedRadio disabled={observableDealIdType !== idOptions.GENERATED} />
                <IdPreview />
              </>
            ),
            labelSx: { width: '100%' },
            value: idOptions.GENERATED,
          },
          {
            'data-test': 'radio-custom',
            label: <IdCustomRadio disabled={observableDealIdType !== idOptions.CUSTOM} />,
            labelSx: { width: '100%' },
            value: idOptions.CUSTOM,
          },
          {
            'data-test': 'radio-external',
            label: (
              <>
                <IdGroupRadio disabled={observableDealIdType !== idOptions.EXTERNAL} />
                <IdGroupPreview disabled={observableDealIdType !== idOptions.EXTERNAL} />
              </>
            ),
            labelSx: { width: '100%' },
            value: idOptions.EXTERNAL,
          },
        ]}
        onChange={updateDealTypeOption}
      />
    </FormSection>
  ) : null;
};
